/**
 * "Tallennetut haut" — manages the per-user saved-search list and exposes
 * helpers used by the listing view to save / load the current filter set.
 *
 * Backend: M6 smt_saved_search_handler.go (CRUD scoped to user_id).
 */
import {
  createSavedSearch,
  deleteSavedSearch,
  listSavedSearches,
  parseFilters,
  type SavedSearch,
} from '../api/saved-searches.ts';
import { APIRequestError } from '../api/client.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';

/**
 * Listener fired when the user picks a saved search from the dropdown in
 * the listing view. The listing module passes a callback that re-applies
 * the filters object to its own state.
 */
export type ApplyFiltersFn = (filters: Record<string, unknown>) => void;

/**
 * Render a small dropdown + "Tallenna haku" button into the host element.
 * Designed to live next to `#btnReset` in the listing filter bar; safe
 * no-op when the caller is not authenticated.
 */
export async function bindSavedSearchControls(host: HTMLElement, opts: {
  currentFilters: () => Record<string, unknown>;
  applyFilters: ApplyFiltersFn;
}): Promise<void> {
  if (!currentUser()) {
    host.innerHTML = '';
    return;
  }

  host.innerHTML = `
    <div class="saved-searches-bar">
      <select class="filter-input" id="savedSearchPick">
        <option value="">${escapeHtml(t('saved_searches_pick'))}</option>
      </select>
      <button class="filter-reset" id="savedSearchSave">${escapeHtml(t('saved_searches_save'))}</button>
      <button class="filter-reset" id="savedSearchDelete" style="display:none">${escapeHtml(t('saved_searches_delete'))}</button>
    </div>
  `;

  const pick = host.querySelector<HTMLSelectElement>('#savedSearchPick')!;
  const saveBtn = host.querySelector<HTMLButtonElement>('#savedSearchSave')!;
  const delBtn = host.querySelector<HTMLButtonElement>('#savedSearchDelete')!;

  let cache: SavedSearch[] = [];
  const refresh = async (): Promise<void> => {
    try {
      cache = await listSavedSearches();
    } catch {
      cache = [];
    }
    pick.innerHTML =
      `<option value="">${escapeHtml(t('saved_searches_pick'))}</option>` +
      cache
        .map((s) => `<option value="${s.id}">${escapeHtml(s.name)}</option>`)
        .join('');
    delBtn.style.display = 'none';
  };
  await refresh();

  pick.addEventListener('change', () => {
    const id = Number(pick.value);
    const row = cache.find((s) => s.id === id);
    if (!row) {
      delBtn.style.display = 'none';
      return;
    }
    delBtn.style.display = '';
    opts.applyFilters(parseFilters(row));
  });

  saveBtn.addEventListener('click', async () => {
    const name = window.prompt(t('saved_searches_name_prompt'));
    if (!name || !name.trim()) return;
    saveBtn.disabled = true;
    try {
      await createSavedSearch({ name: name.trim(), filters: opts.currentFilters() });
      await refresh();
    } catch (err) {
      window.alert(formatErr(err));
    } finally {
      saveBtn.disabled = false;
    }
  });

  delBtn.addEventListener('click', async () => {
    const id = Number(pick.value);
    if (!id) return;
    if (!window.confirm(t('saved_searches_delete_confirm'))) return;
    delBtn.disabled = true;
    try {
      await deleteSavedSearch(id);
      pick.value = '';
      await refresh();
    } catch (err) {
      window.alert(formatErr(err));
    } finally {
      delBtn.disabled = false;
    }
  });
}

/**
 * Standalone view rendered at `#/saved-searches` — lists the user's saved
 * searches with delete buttons. Saving and applying happens from the
 * listing view (above), this page is just for housekeeping.
 */
export async function renderSavedSearchesPage(root: HTMLElement): Promise<void> {
  if (!currentUser()) {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('login_required'))}</div></div>`;
    return;
  }
  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('saved_searches_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('saved_searches_sub'))}</div>
      <div id="savedSearchesList">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  try {
    const rows = await listSavedSearches();
    const list = root.querySelector<HTMLDivElement>('#savedSearchesList')!;
    if (!rows.length) {
      list.innerHTML = `<div class="placeholder-card">${escapeHtml(t('saved_searches_empty'))}</div>`;
      return;
    }
    list.innerHTML = `
      <table class="listing-table">
        <thead><tr>
          <th>${escapeHtml(t('saved_searches_th_name'))}</th>
          <th>${escapeHtml(t('saved_searches_th_filters'))}</th>
          <th>${escapeHtml(t('saved_searches_th_updated'))}</th>
          <th>${escapeHtml(t('view_admin_th_actions'))}</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              (s) => `<tr>
                <td>${escapeHtml(s.name)}</td>
                <td><code style="font-size:11px">${escapeHtml(filtersSummary(s))}</code></td>
                <td>${escapeHtml(formatDate(s.updated_at))}</td>
                <td><button class="btn-reject" data-id="${s.id}">${escapeHtml(t('saved_searches_delete'))}</button></td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    `;
    list.querySelectorAll<HTMLButtonElement>('button[data-id]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!window.confirm(t('saved_searches_delete_confirm'))) return;
        btn.disabled = true;
        try {
          await deleteSavedSearch(Number(btn.dataset.id));
          await renderSavedSearchesPage(root);
        } catch (err) {
          window.alert(formatErr(err));
          btn.disabled = false;
        }
      });
    });
  } catch (err) {
    root.querySelector<HTMLDivElement>('#savedSearchesList')!.innerHTML =
      `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(err))}</div>`;
  }
}

function filtersSummary(s: SavedSearch): string {
  const obj = parseFilters(s);
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || v === '' || v === 0) continue;
    parts.push(`${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
  }
  return parts.length ? parts.join(' · ') : '∅';
}

function formatDate(s: string): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: unknown): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
